/**
 * RoadClosureLayer — renders road closure markers on the map.
 * Also provides a report dialog for new closures.
 */
import { Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { useEffect, useRef } from 'react';
import L from 'leaflet';

const closureIcon = L.divIcon({
  className: '',
  html: `<div style="width:28px;height:28px;background:#ef4444;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3)">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

export const RoadClosureMarkers = ({ closures, visible, onUpvote }) => {
  if (!visible || !closures?.length) return null;

  return (
    <>
      {closures.map(c => (
        <Marker key={c.closure_id} position={[c.lat, c.lng]} icon={closureIcon}>
          <Popup className="crisis-popup">
            <div style={{ minWidth: '160px' }}>
              <p style={{ fontWeight: 600, fontSize: '12px', marginBottom: '4px', color: '#ef4444' }}>
                Road Closed
              </p>
              {c.reason && <p style={{ fontSize: '11px', color: '#666', marginBottom: '2px', textTransform: 'capitalize' }}>{c.reason}</p>}
              {c.description && <p style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>{c.description}</p>}
              <p style={{ fontSize: '9px', color: '#aaa' }}>
                Reported by {c.reported_by_name} · {c.upvotes} confirm{c.upvotes !== 1 ? 's' : ''}
              </p>
              {onUpvote && (
                <button
                  onClick={() => onUpvote(c.closure_id)}
                  style={{ marginTop: '4px', fontSize: '10px', color: '#ef4444', fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                >
                  Confirm closure
                </button>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
      {/* Draw lines for closures with two points */}
      {closures.filter(c => c.lat2 && c.lng2).map(c => (
        <Polyline
          key={`line-${c.closure_id}`}
          positions={[[c.lat, c.lng], [c.lat2, c.lng2]]}
          pathOptions={{ color: '#ef4444', weight: 6, opacity: 0.7, dashArray: '10, 10' }}
        />
      ))}
    </>
  );
};

export const EvacuationRouteLines = ({ routes, visible }) => {
  if (!visible || !routes?.length) return null;

  return (
    <>
      {routes.map(r => {
        const positions = r.waypoints?.map(w => [w.lat, w.lng]) || [];
        if (positions.length < 2) return null;
        const isEvac = r.corridor_type === 'evacuation';
        return (
          <Polyline
            key={r.route_id}
            positions={positions}
            pathOptions={{
              color: isEvac ? '#22c55e' : '#3b82f6',
              weight: 6,
              opacity: 0.75,
              dashArray: isEvac ? '12, 6' : undefined,
            }}
          >
            <Popup className="crisis-popup">
              <div style={{ minWidth: '140px' }}>
                <p style={{ fontWeight: 600, fontSize: '12px', color: isEvac ? '#22c55e' : '#3b82f6' }}>
                  {r.name}
                </p>
                <p style={{ fontSize: '10px', color: '#888', textTransform: 'capitalize' }}>{r.corridor_type}</p>
                {r.description && <p style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{r.description}</p>}
              </div>
            </Popup>
          </Polyline>
        );
      })}
    </>
  );
};
